'use client'

import { Box, Container, Flex, Heading, HStack, Stack, Text, VStack } from '@chakra-ui/react'
import Navigator from '@/app/[locale]/(public)/propiedades/[slug]/_components/Navigator'
import React, { FC } from 'react'
import ShareLinkModal from '@/app/components/modals/ShareLinkModal'
import PrintButton from '@/app/[locale]/(public)/propiedades/[slug]/_components/PrintButton'
import { formatNumber } from '@/utils/format-number'
import RealEstateDetailMap from '@/app/[locale]/(public)/propiedades/[slug]/_components/RealEstateDetailMap'
import RealEstateDetailContact from '@/app/[locale]/(public)/propiedades/[slug]/_components/RealEstateDetailContact'
import RealEstateDetails from '@/app/[locale]/(public)/propiedades/[slug]/_components/RealEstateDetails'
import { NavigationListElement, RealEstateDTO, RealEstateLightDTO } from '@/core/real-estate/typings'
import { formatPriceString } from '@/utils/format-price-string'
import ContactButton from '@/app/[locale]/(public)/propiedades/[slug]/_components/ContactButton'
import RealEstateInArea from './RealEstateInArea'

interface RealEstateDetailProps {
  data: RealEstateDTO
  deviceType: 'desktop' | 'mobile'
  currentSlug: string
  navigationList: NavigationListElement[]
  queryString: string
  realEstateInAreaData?: RealEstateLightDTO[]
}

const RealEstateDetail: FC<RealEstateDetailProps> = ({
  data,
  deviceType,
  currentSlug,
  navigationList,
  queryString,
  realEstateInAreaData
}) => {
  if (!data.available) {
    return (
      <Flex w="full" h="90vh" alignItems="center" justifyContent="center">
        <Text color="red.500">Esta propiedad no está disponible</Text>
      </Flex>
    )
  }

  return (
    <VStack spacing={8} w="full">
      <Navigator
        deviceType={deviceType}
        currentSlug={currentSlug}
        navigationList={navigationList}
        queryString={queryString}
      />

      <Container
        as={deviceType === 'mobile' ? VStack : HStack}
        alignItems="flex-start"
        px={4}
        py={2}
        maxW="container.xl"
        w="full">
        <VStack w={{ base: 'full', md: 'auto' }} align="flex-start" flex={{ base: 1, md: 0.7 }}>
          <Heading fontSize="3xl" as="h1">
            {data.name}
          </Heading>
          <Heading fontSize="2xl" as="h2" fontWeight="normal">
            {data.address.formatted}
          </Heading>
        </VStack>

        {!data.price?.rent?.price && !data.price?.sale?.price ? (
          <Stack
            flexDir={{ base: 'row', md: 'column' }}
            flex={{ base: 1, md: 0.3 }}
            spacing={{ base: 4, md: 1 }}
            w={{ base: 'full', md: 'auto' }}>
            <Heading as="h6" fontSize="xl">
              Precio
            </Heading>
            <Text fontSize="lg" fontWeight="medium">
              Contactar al broker
            </Text>
          </Stack>
        ) : (
          <HStack
            py={{ base: 2, md: 4 }}
            px={{ base: 0, md: 4 }}
            w={{ base: 'full', md: 'auto' }}
            flex={{ base: 1, md: 0.3 }}>
            {data.price.rent?.price ? (
              <Stack flexDir={{ base: 'row', md: 'column' }} flex={1}>
                <Heading as="h6" fontSize="xl">
                  Renta
                </Heading>
                <Text
                  fontSize={{ base: 'md', md: 'lg' }}
                  fontWeight={{ base: 'medium', md: 'bold' }}>
                  {formatPriceString(formatNumber(data.price.rent.price), {
                    label: data.price.rent.label,
                    currency: data.price.rent.currency,
                    showPrefix: true
                  })}
                </Text>
              </Stack>
            ) : null}

            {data.price.sale?.price ? (
              <Stack flexDir={{ base: 'row', md: 'column' }} flex={1}>
                <Heading as="h6" fontSize={{ base: 'lg', md: 'lg' }}>
                  Venta
                </Heading>
                <Text
                  fontSize={{ base: 'md', md: 'lg' }}
                  fontWeight={{ base: 'medium', md: 'bold' }}>
                  {formatPriceString(formatNumber(data.price.sale.price), {
                    label: data.price.sale.label,
                    currency: data.price.sale.currency,
                    showPrefix: true
                  })}
                </Text>
              </Stack>
            ) : null}
          </HStack>
        )}

        <HStack
          alignSelf="stretch"
          flex={{ base: 1, md: 0 }}
          align={{ base: 'flex-start', md: 'center' }}
          justify={{ base: 'flex-start', md: 'center' }}>
          <PrintButton boxSize={8} />
          <ShareLinkModal boxSize={8} />
          <ContactButton display={{ md: 'none' }} />
        </HStack>
      </Container>

      <Container
        gap={12}
        as={deviceType === 'mobile' ? VStack : HStack}
        alignItems="flex-start"
        px={4}
        py={2}
        maxW="container.xl"
        w="full">
        <Box w="full" overflowX="hidden" flex={0.7}>
          <RealEstateDetails data={data} />
        </Box>

        <Box alignSelf="stretch" flex={0.3}>
          <RealEstateDetailContact id="contacto" realEstate={data} brokers={data.brokers} />
        </Box>
      </Container>

      <RealEstateDetailMap location={data.location} />

      {realEstateInAreaData?.length ? (
        <Container gap={12} maxW="container.xl" w="full" pb={6}>
          <RealEstateInArea data={realEstateInAreaData} />
        </Container>
      ) : null}
    </VStack>
  )
}

export default RealEstateDetail
